import axios from "axios";
import { IServiceHistory, IServiceHistoryRequest } from "../interfaces/service-history";
import fetchTimeout from "../utils/fetchTimeout";
import { decodeTokenLS } from "../utils/token";
import { setHeaders, URL_REQUEST } from "./constants";

export async function getServiceHistory(): Promise<IServiceHistory> {

    let response: IServiceHistoryRequest;
    let employeeId;
    let localId;
    const decoded = decodeTokenLS();
    if (decoded) {
        employeeId = decoded.user.empleadoId;
        localId = decoded.user.localId;

        const raw = JSON.stringify({
            localId: localId,
            empleadoId: employeeId,
        });

        let res: any = await fetchTimeout(URL_REQUEST + "/api/v1/admin/comanda/mvp/listarComandasFinalizadas", {
            "headers": setHeaders(),
            "body": raw,
            "method": "post",
        });

        response = await res.json();
        if (response.status === "200") {
            return response.data;
        }
        throw new Error(response.message);
    }
    throw new Error("Token Invalido, por favor vuelva a iniciar sesion");

}

export async function getServiceHistoryByDates(startDate: any, endDate: any): Promise<any> {
    // Migrado 2026-05-20: el endpoint /resumencomandasfecha nunca existió en el backend
    // (404 garantizado, ver caso SOHO Schell). Usamos /listarComandasFinalizadas que
    // sí existe y devuelve los servicios del estilista logueado.
    const decoded = decodeTokenLS();
    if (!decoded) {
        throw new Error("Token inválido, por favor vuelva a iniciar sesión");
    }
    const { token, user } = decoded;

    const response = await axios.post(
        URL_REQUEST + '/api/v1/admin/comanda/mvp/listarComandasFinalizadas',
        {
            fechaInicio: startDate,
            fechaFin: endDate,
            empresaId: user?.empresaId,
            localId: user?.localId,
            empleadoId: user?.empleadoId,
        },
        { headers: { "x-access-token": token } }
    ).then(res => res.data);

    if (response.status !== "200" || !response.data) {
        // El componente que llama trata el throw como error, así que solo lanzamos
        // para errores reales del backend (5xx, mensaje específico).
        throw new Error(response.message || "No se pudo cargar el historial");
    }

    // Mapeo de campos: la card del historial (card-historial.tsx) espera campos
    // con nombres específicos. Adaptamos la respuesta de listarComandasFinalizadas
    // a esos nombres para que renderice correctamente.
    // Nota: la card pasa fechaCreacion a getDay() que ya recibe "DD Mes" + año
    // automáticamente. Usamos diaFinalizacion del backend que ya viene en ese
    // formato ("20 May"), en vez de fechaCreacion (que viene "DD/MM/YYYY HH:mm:ss"
    // y rompe new Date()).
    const rawDetalle: any[] = response.data.detalle || [];
    const detalle = rawDetalle.map(d => ({
        ...d,
        nombre: d.cliente || `${d.clienteNombre || ''} ${d.clienteApellido || ''}`.trim(),
        nombreServicio: d.servicio,
        nombreProducto: d.producto || null,
        horaFin: d.horaFinalizacion || '',
        servicios: [{ servicio: d.servicio }],
        // Sobrescribimos fechaCreacion con diaFinalizacion ("20 May") porque es lo
        // que getDay() puede parsear correctamente.
        fechaCreacion: d.diaFinalizacion || d.fechaCreacion,
    }));
    const comandaPagada = detalle.filter(d => d.cobrada === 1);
    const comandaPendientePago = detalle.filter(d => d.cobrada !== 1);

    // Ventas directas: servicios cobrados desde POS sin pasar por una comanda
    // (caso Gambino's). Aplicamos el mismo mapping que al detalle de comandas
    // para que card-historial las renderice correctamente.
    const rawVentasDirectas: any[] = response.data.ventasDirectas || [];
    const ventasDirectasMapped = rawVentasDirectas.map(d => ({
        ...d,
        nombre: d.cliente || `${d.clienteNombre || ''} ${d.clienteApellido || ''}`.trim(),
        nombreServicio: d.servicio,
        nombreProducto: d.producto || null,
        horaFin: d.horaFinalizacion || '',
        servicios: [{ servicio: d.servicio }],
        fechaCreacion: d.diaFinalizacion || d.fechaCreacion,
    }));

    return {
        totalServicios: response.data.totalServicios,
        cantidadServicios: response.data.cantidadServicios,
        totalProductos: response.data.totalProductos,
        cantidadProductos: response.data.cantidadProductos,
        detalle,
        comandaPagada,
        comandaPendientePago,
        ventasDirectas: ventasDirectasMapped,
        totalVentasDirectas: response.data.totalVentasDirectas || 0,
        cantidadVentasDirectas: response.data.cantidadVentasDirectas || 0,
    };
}

export async function getServiceHistoryByDates2(startDate: any, endDate: any): Promise<any> {
    // Migrado 2026-05-20: el endpoint /reportedetalladofecha nunca existió en el backend
    // (404 garantizado, ver caso SOHO). Usamos /listarComandasFinalizadas que sí existe
    // y ahora respeta fechaInicio/fechaFin tras el parche backend del mismo día.
    const decoded = decodeTokenLS();
    if (!decoded) {
        throw new Error("Token inválido, por favor vuelva a iniciar sesión");
    }
    const { token, user } = decoded;

    const response = await axios.post(
        URL_REQUEST + '/api/v1/admin/comanda/mvp/listarComandasFinalizadas',
        {
            fechaInicio: startDate,
            fechaFin: endDate,
            empresaId: user?.empresaId,
            localId: user?.localId,
            empleadoId: user?.empleadoId,
        },
        { headers: { "x-access-token": token } }
    ).then(res => res.data);

    if (response.status !== "200" || !response.data) {
        throw new Error(response.message || "No se pudo cargar el historial");
    }

    // Mapeo idéntico al de getServiceHistoryByDates para que la card del historial
    // (card-historial.tsx) renderice correctamente.
    const rawDetalle: any[] = response.data.detalle || [];
    const detalle = rawDetalle.map(d => ({
        ...d,
        nombre: d.cliente || `${d.clienteNombre || ''} ${d.clienteApellido || ''}`.trim(),
        nombreServicio: d.servicio,
        nombreProducto: d.producto || null,
        horaFin: d.horaFinalizacion || '',
        servicios: [{ servicio: d.servicio }],
        fechaCreacion: d.diaFinalizacion || d.fechaCreacion,
    }));
    const comandaPagada = detalle.filter(d => d.cobrada === 1);
    const comandaPendientePago = detalle.filter(d => d.cobrada !== 1);

    // Ventas directas: servicios cobrados desde POS sin pasar por una comanda
    // (caso Gambino's). Mismo mapping que el detalle para reutilizar la card.
    const rawVentasDirectas: any[] = response.data.ventasDirectas || [];
    const ventasDirectasMapped = rawVentasDirectas.map(d => ({
        ...d,
        nombre: d.cliente || `${d.clienteNombre || ''} ${d.clienteApellido || ''}`.trim(),
        nombreServicio: d.servicio,
        nombreProducto: d.producto || null,
        horaFin: d.horaFinalizacion || '',
        servicios: [{ servicio: d.servicio }],
        fechaCreacion: d.diaFinalizacion || d.fechaCreacion,
    }));

    return {
        totalServicios: response.data.totalServicios,
        cantidadServicios: response.data.cantidadServicios,
        totalProductos: response.data.totalProductos,
        cantidadProductos: response.data.cantidadProductos,
        detalle,
        comandaPagada,
        comandaPendientePago,
        ventasDirectas: ventasDirectasMapped,
        totalVentasDirectas: response.data.totalVentasDirectas || 0,
        cantidadVentasDirectas: response.data.cantidadVentasDirectas || 0,
    };
}